// Conditional display + validation for survey questions.
// Mirrors messaging/api/app/surveys/rules.py - keep the two in sync.
// Rules live in question.question_rules as a JSON string; questions without
// rules behave exactly as before.

export const parseRules = (raw) => {
  if (!raw) return null;
  if (typeof raw === "object") return raw;
  try {
    const data = JSON.parse(raw);
    return data && typeof data === "object" && !Array.isArray(data) ? data : null;
  } catch (e) {
    return null;
  }
};

const text = (answer) =>
  answer === undefined || answer === null
    ? ""
    : Array.isArray(answer)
    ? answer.join(", ").trim()
    : String(answer).trim();

const toNumber = (value) => {
  const n = parseFloat(String(value).trim());
  return Number.isNaN(n) ? null : n;
};

const conditionMet = (cond, answers, typeByIndex) => {
  const answer = text(answers[cond.question]);
  if (cond.op === "answered") return answer !== "";
  if (cond.op === "not_answered") return answer === "";

  const expected = text(cond.value).toLowerCase();
  const lowered = answer.toLowerCase();

  switch (cond.op) {
    case "equals":
      return lowered === expected;
    case "not_equals":
      return lowered !== expected;
    case "contains":
      return typeByIndex[cond.question] === "checkboxes"
        ? lowered.split(",").map((t) => t.trim()).includes(expected)
        : lowered.includes(expected);
    case "gt":
    case "lt": {
      const left = toNumber(answer);
      const right = toNumber(expected);
      if (left === null || right === null) return false;
      return cond.op === "gt" ? left > right : left < right;
    }
    default:
      return false;
  }
};

const isVisible = (rules, answers, typeByIndex) => {
  const showIf = rules?.showIf;
  if (!showIf?.conditions?.length) return true;
  const results = showIf.conditions.map((c) => conditionMet(c, answers, typeByIndex));
  return showIf.match === "any" ? results.some(Boolean) : results.every(Boolean);
};

/**
 * Returns the questions that should currently be shown. A hidden question
 * counts as unanswered for the questions that depend on it.
 */
export const getVisibleQuestions = (questions = [], answers = {}) => {
  const typeByIndex = {};
  questions.forEach((q) => {
    typeByIndex[q.question_index] = q.question_type;
  });

  const hidden = new Set();
  [...questions]
    .sort((a, b) => a.question_index - b.question_index)
    .forEach((q) => {
      const rules = parseRules(q.question_rules);
      if (!rules) return;
      const visibleAnswers = {};
      Object.keys(answers).forEach((i) => {
        if (!hidden.has(Number(i))) visibleAnswers[i] = answers[i];
      });
      if (!isVisible(rules, visibleAnswers, typeByIndex)) hidden.add(q.question_index);
    });

  return questions.filter((q) => !hidden.has(q.question_index));
};

/** Returns an error message for the answer, or null when valid/empty. */
export const validateAnswer = (question, answer) => {
  const validation = parseRules(question.question_rules)?.validation;
  const value = text(answer);
  if (!validation || value === "") return null;

  const { message, minLength, maxLength, pattern, min, max } = validation;

  if (minLength != null && value.length < minLength)
    return message || `Must be at least ${minLength} characters`;
  if (maxLength != null && value.length > maxLength)
    return message || `Must be at most ${maxLength} characters`;

  if (pattern) {
    try {
      if (!new RegExp(pattern).test(value)) return message || "Invalid format";
    } catch (e) {
      // ignore a bad pattern rather than blocking the form
    }
  }

  if (min != null || max != null) {
    if (["date", "time"].includes(question.question_type)) {
      if (min != null && value < String(min)) return message || `Must be on or after ${min}`;
      if (max != null && value > String(max)) return message || `Must be on or before ${max}`;
    } else {
      const n = toNumber(value);
      if (n === null) return message || "Enter a valid number";
      if (min != null && n < min) return message || `Must be at least ${min}`;
      if (max != null && n > max) return message || `Must be at most ${max}`;
    }
  }
  return null;
};

/** Map of question_index -> error message for every visible question. */
export const getValidationErrors = (visibleQuestions = [], answers = {}) => {
  const errors = {};
  visibleQuestions.forEach((q) => {
    const err = validateAnswer(q, answers[q.question_index]);
    if (err) errors[q.question_index] = err;
  });
  return errors;
};
